import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GitMerge, Loader2, MoreHorizontal } from "lucide-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type GetRowIdParams,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IRowNode,
  type RowSelectionChangedEvent,
} from "ag-grid-community";
import { RowGroupingModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { instagramProfileUrlFromHandle } from "@/lib/instagramProfileUrl";

ModuleRegistry.registerModules([AllCommunityModule, RowGroupingModule]);

interface DuplicateLead {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  instagram?: {
    igUserId?: string;
    igUsername?: string;
  };
  manychat?: {
    id?: string;
  };
  joinedAt?: string;
}

interface DuplicateGroup {
  igUserId: string;
  leads: DuplicateLead[];
  mergedLead: DuplicateLead | null;
}

interface InstagramMergeRow extends DuplicateLead {
  igUserId: string;
}

interface InstagramMergeGridContext {
  isMerging: boolean;
  mergedLeadsByIgUserId: Map<string, DuplicateLead>;
  onMergeSingle: (igUserId: string) => void;
}

function getLeadName(lead: DuplicateLead): string {
  const parts = [lead.firstName, lead.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (lead.name) return lead.name;
  return "-";
}

function getRowLead({
  data,
  node,
  context,
}: {
  data?: InstagramMergeRow;
  node?: IRowNode<InstagramMergeRow> | null;
  context?: InstagramMergeGridContext;
}): DuplicateLead | undefined {
  if (data) return data;
  if (node?.group && node.key) {
    return context?.mergedLeadsByIgUserId.get(node.key);
  }
  return undefined;
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ActionsCellRenderer({
  node,
  context,
}: ICellRendererParams<InstagramMergeRow, unknown, InstagramMergeGridContext>) {
  if (!node.group || !node.key) return null;

  return (
    <div className="flex h-full items-center justify-end" onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={`Actions for ${node.key}`}
            disabled={context?.isMerging}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => context?.onMergeSingle(node.key!)}
            disabled={context?.isMerging}
          >
            <GitMerge className="h-4 w-4" />
            Merge
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function ContactsInstagramMerge() {
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const gridApiRef = useRef<GridApi<InstagramMergeRow> | null>(null);
  const [groups, setGroups] = useState<DuplicateGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMerging, setIsMerging] = useState(false);
  const [selectedIgUserIds, setSelectedIgUserIds] = useState<Set<string>>(new Set());

  const fetchDuplicates = useCallback(async () => {
    try {
      const response = await fetch("/api/contacts/instagram/merge");
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      } else {
        console.error("Failed to fetch Instagram merge duplicates");
      }
    } catch (error) {
      console.error("Failed to fetch Instagram merge duplicates:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Contacts", path: "/contacts" },
      { label: "Instagram Merge", path: "/contacts/instagram/merge" },
    ]);

    return () => {
      clearBreadcrumbs();
    };
  }, [setBreadcrumbs, clearBreadcrumbs]);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  const mergeIgUserIds = useCallback(
    async (igUserIds: string[]) => {
      if (igUserIds.length === 0 || isMerging) return;

      const confirmed = window.confirm(
        igUserIds.length === 1
          ? "Are you sure you want to merge the duplicate contacts for this Instagram user? This action cannot be undone."
          : `Are you sure you want to merge ${igUserIds.length} duplicate Instagram users? This action cannot be undone.`,
      );
      if (!confirmed) return;

      setIsMerging(true);
      try {
        const response = await fetch("/api/contacts/instagram/merge", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ igUserIds }),
        });

        if (response.ok) {
          setSelectedIgUserIds(new Set());
          gridApiRef.current?.deselectAll();
          setIsLoading(true);
          await fetchDuplicates();
        } else {
          console.error("Failed to merge Instagram duplicate leads");
        }
      } catch (error) {
        console.error("Failed to merge Instagram duplicate leads:", error);
      } finally {
        setIsMerging(false);
      }
    },
    [fetchDuplicates, isMerging],
  );

  const handleMergeSelected = useCallback(() => {
    mergeIgUserIds([...selectedIgUserIds]);
  }, [mergeIgUserIds, selectedIgUserIds]);

  const handleMergeSingle = useCallback(
    (igUserId: string) => {
      mergeIgUserIds([igUserId]);
    },
    [mergeIgUserIds],
  );

  const rowData = useMemo(
    () =>
      groups.flatMap((group) =>
        group.leads.map((lead) => ({
          ...lead,
          igUserId: group.igUserId,
        })),
      ),
    [groups],
  );

  const mergedLeadsByIgUserId = useMemo(
    () =>
      new Map(
        groups.flatMap((group) =>
          group.mergedLead ? [[group.igUserId, group.mergedLead] as const] : [],
        ),
      ),
    [groups],
  );

  const gridContext = useMemo<InstagramMergeGridContext>(
    () => ({
      isMerging,
      mergedLeadsByIgUserId,
      onMergeSingle: handleMergeSingle,
    }),
    [handleMergeSingle, isMerging, mergedLeadsByIgUserId],
  );

  const autoGroupColumnDef = useMemo<ColDef<InstagramMergeRow>>(
    () => ({
      headerName: "Instagram User ID",
      minWidth: 220,
      flex: 1,
      cellClass: "font-mono text-sm",
    }),
    [],
  );

  const columnDefs = useMemo<ColDef<InstagramMergeRow>[]>(
    () => [
      {
        field: "igUserId",
        rowGroup: true,
        hide: true,
      },
      {
        headerName: "Name",
        flex: 1,
        minWidth: 140,
        valueGetter: ({ data, node, context }) => {
          const lead = getRowLead({ data, node, context });
          if (!lead) return "-";
          const name = getLeadName(lead);
          return name !== "-" ? name : "-";
        },
        cellRenderer: ({ data, node, context }: ICellRendererParams<
          InstagramMergeRow,
          unknown,
          InstagramMergeGridContext
        >) => {
          const lead = getRowLead({ data, node, context });
          if (!lead) return "-";

          const name = getLeadName(lead);
          if (name === "-") return "-";

          if (data) {
            return (
              <Link
                to={`/contacts/${data._id}/edit`}
                className="text-primary underline underline-offset-4"
              >
                {name}
              </Link>
            );
          }

          return (
            <Link
              to={`/contacts/${lead._id}/edit`}
              className="text-primary underline underline-offset-4"
            >
              {name}
            </Link>
          );
        },
      },
      {
        headerName: "Instagram",
        flex: 1,
        minWidth: 120,
        valueGetter: ({ data, node, context }) => {
          const lead = getRowLead({ data, node, context });
          return lead?.instagram?.igUsername || "-";
        },
        cellRenderer: ({ data, node, context }: ICellRendererParams<
          InstagramMergeRow,
          unknown,
          InstagramMergeGridContext
        >) => {
          const lead = getRowLead({ data, node, context });
          const handle = lead?.instagram?.igUsername;
          const href = handle ? instagramProfileUrlFromHandle(handle) : null;

          if (!handle || !href) return "-";

          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-4"
            >
              {handle}
            </a>
          );
        },
      },
      {
        headerName: "Email",
        flex: 1,
        minWidth: 160,
        valueGetter: ({ data, node, context }) => getRowLead({ data, node, context })?.email || "-",
      },
      {
        headerName: "Phone",
        flex: 1,
        minWidth: 120,
        valueGetter: ({ data, node, context }) =>
          getRowLead({ data, node, context })?.phoneNumber || "-",
      },
      {
        headerName: "Manychat",
        flex: 1,
        minWidth: 120,
        valueGetter: ({ data, node, context }) =>
          getRowLead({ data, node, context })?.manychat?.id || "-",
      },
      {
        headerName: "Joined",
        width: 120,
        valueGetter: ({ data, node, context }) => {
          const joinedAt = getRowLead({ data, node, context })?.joinedAt;
          return joinedAt ? formatDate(joinedAt) : "-";
        },
      },
      {
        colId: "actions",
        headerName: "",
        width: 56,
        maxWidth: 56,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        cellRenderer: ActionsCellRenderer,
      },
    ],
    [],
  );

  const defaultColDef = useMemo<ColDef<InstagramMergeRow>>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressMovable: true,
    }),
    [],
  );

  const handleGridReady = useCallback(({ api }: GridReadyEvent<InstagramMergeRow>) => {
    gridApiRef.current = api;
  }, []);

  const handleSelectionChanged = useCallback((event: RowSelectionChangedEvent<InstagramMergeRow>) => {
    const selectedIds = event.api
      .getSelectedNodes()
      .filter((node) => node.group && node.key)
      .map((node) => node.key!);

    setSelectedIgUserIds(new Set(selectedIds));
  }, []);

  const getRowId = useCallback(({ data }: GetRowIdParams<InstagramMergeRow>) => {
    if (!data) return "";
    return data._id;
  }, []);

  const isRowSelectable = useCallback(
    (node: IRowNode<InstagramMergeRow>) => {
      return Boolean(node?.group) && !isMerging;
    },
    [isMerging],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Instagram Merge</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {groups.length} duplicate Instagram user{groups.length === 1 ? "" : "s"}
            {selectedIgUserIds.size > 0 ? ` · ${selectedIgUserIds.size} selected` : ""}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Merge actions"
              disabled={isMerging}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={handleMergeSelected}
              disabled={selectedIgUserIds.size === 0 || isMerging}
            >
              <GitMerge className="h-4 w-4" />
              Merge
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="ag-theme-quartz w-full" style={{ height: "calc(100vh - 12rem)" }}>
        <AgGridReact<InstagramMergeRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDefaultExpanded={0}
          context={gridContext}
          getRowId={getRowId}
          rowSelection={{
            mode: "multiRow",
            checkboxes: true,
            headerCheckbox: true,
            enableClickSelection: false,
            isRowSelectable,
          }}
          onGridReady={handleGridReady}
          onSelectionChanged={handleSelectionChanged}
          overlayNoRowsTemplate="No duplicate Instagram user IDs found"
          suppressCellFocus
          enableCellTextSelection
        />
      </div>
    </div>
  );
}
